import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { fileApi } from '@services/apiService';

// 附件类型
export interface Attachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  preview?: string;
  projectId?: string;
  taskId?: string;
  issueId?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface AttachmentState {
  // 状态
  attachments: Attachment[];
  loading: boolean;
  error: string | null;
  
  // 方法
  // 附件管理
  fetchAttachments: (params?: {
    projectId?: string;
    taskId?: string;
    issueId?: string;
  }) => Promise<void>;
  uploadAttachment: (file: File, params?: {
    projectId?: string;
    taskId?: string;
    issueId?: string;
  }) => Promise<Attachment>;
  deleteAttachment: (id: string) => Promise<void>;
  getAttachmentById: (id: string) => Attachment | undefined;
  getAttachmentsByParentId: (parentId: string, type: 'project' | 'task' | 'issue') => Attachment[];
  
  // 重置状态
  reset: () => void;
}

export const useAttachmentStore = create<AttachmentState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      attachments: [],
      loading: false,
      error: null,

      // 获取附件列表
      fetchAttachments: async (params) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 获取附件列表
          // 模拟数据
          const mockAttachments: Attachment[] = [
            {
              id: '1',
              name: '项目需求文档.pdf',
              url: 'https://example.com/files/project-requirements.pdf',
              size: 1024 * 1024 * 2.5, // 2.5MB
              type: 'application/pdf',
              projectId: params?.projectId,
              userId: '1',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: '2',
              name: '技术架构图.png',
              url: 'https://example.com/files/architecture.png',
              size: 1024 * 1024 * 1.8, // 1.8MB
              type: 'image/png',
              preview: 'https://example.com/files/architecture.png',
              projectId: params?.projectId,
              userId: '1',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: '3',
              name: '任务分解.xlsx',
              url: 'https://example.com/files/task-breakdown.xlsx',
              size: 1024 * 1024 * 1.2, // 1.2MB
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              taskId: params?.taskId,
              userId: '1',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
          set({ attachments: mockAttachments, loading: false });
        } catch (error) {
          set({ error: '获取附件失败', loading: false });
          console.error('获取附件失败:', error);
        }
      },

      // 上传附件
      uploadAttachment: async (file, params) => {
        set({ loading: true, error: null });
        try {
          const uploadedFile = await fileApi.uploadFile(file);
          
          // TODO: 调用 API 保存附件关联信息
          const newAttachment: Attachment = {
            id: uploadedFile.id,
            name: uploadedFile.name,
            url: uploadedFile.url,
            size: uploadedFile.size,
            type: uploadedFile.type,
            projectId: params?.projectId,
            taskId: params?.taskId,
            issueId: params?.issueId,
            userId: '1', // 当前用户 ID
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          set((state) => ({
            attachments: [newAttachment, ...state.attachments],
            loading: false,
          }));
          
          return newAttachment;
        } catch (error) {
          set({ error: '上传附件失败', loading: false });
          console.error('上传附件失败:', error);
          throw error;
        }
      },

      // 删除附件
      deleteAttachment: async (id) => {
        set({ loading: true, error: null });
        try {
          await fileApi.deleteFile(id);
          set((state) => ({
            attachments: state.attachments.filter(attachment => attachment.id !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: '删除附件失败', loading: false });
          console.error('删除附件失败:', error);
          throw error;
        }
      },

      // 根据 ID 获取附件
      getAttachmentById: (id) => {
        return get().attachments.find(attachment => attachment.id === id);
      },

      // 根据父 ID 获取附件
      getAttachmentsByParentId: (parentId, type) => {
        const { attachments } = get();
        switch (type) {
          case 'project':
            return attachments.filter(attachment => attachment.projectId === parentId);
          case 'task':
            return attachments.filter(attachment => attachment.taskId === parentId);
          case 'issue':
            return attachments.filter(attachment => attachment.issueId === parentId);
          default:
            return [];
        }
      },

      // 重置状态
      reset: () => {
        set({
          attachments: [],
          loading: false,
          error: null,
        });
      },
    })
  )
);